import { Modal, Spin } from 'antd';
import { result } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  getCustomerTypesSegment,
  getInvestListData,
  sendToCMAlerts
} from '../../../../redux/actions/ns/investigation';
import {
  selectedAlertIds,
  updateAlertListPage,
  updateAlertListPageSize
} from '../../../../redux/actions/ns/onDemandScreening';
import { getNSGlobalSettingsPageInfo } from '../../../../redux/actions/settings';
import TablePagination from '../../../cm/common/TablePagination';
import ComponentsMap from '../../../common/ComponentsMap';
import {
  btoaUTF8,
  genarateTableConfigs,
  getGlobalSettings
} from '../../../common/utils';
import { PAGE_SIZE_OPTIONS } from '../../../constants/cmConstants';
import AlertListFilters from './AlertListFilters';
import Drawer from './Drawer';
import { StyledTable } from './styled';
import { StyledAlert } from '../styled';
import { prepareAlertHistoryFilters } from '../Utils';
import { ROUTE_AMLS_SMART_SCREENING } from '../../../constants/globalConstants';

const { confirm } = Modal;

const AlertList = ({
  dispatch,
  history,
  location,
  settings,
  isLoading,
  currentPage,
  pageSize,
  alertList,
  total,
  isLoadingAlerts,
  hasError,
  errorMsg,
  customerType,
  selectedIds,
  selectedAlerts,
  filters,
  activeTab
}) => {
  const [jobId, setJobId] = useState(null);
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [currentDrawerItem, setCurrentDrawerItem] = useState('');
  const [runInstanceId, setRunInstanceId] = useState('');
  const [segmentCode, setSegmentCode] = useState('');

  const { widgetConfig, isVisible, widgetId } = useMemo(
    () =>
      getGlobalSettings({
        settings,
        gsQuery: {
          widgetGroupId: 'ON_DEMAND_NS_ALERTS_SUMMARY_PANEL',
          widgetId: 'ON_DEMAND_NS_ALERTS_SUMMARY_PANEL_WIDGET'
        }
      }),
    [settings]
  );

  useEffect(() => {
    if (location?.state?.jobId) {
      setJobId(location.state.jobId);
    } else {
      setJobId(null);
    }
  }, [location]);

  useEffect(() => {
    if (widgetId !== 'ON_DEMAND_NS_ALERTS_SUMMARY_PANEL_WIDGET') {
      dispatch(
        getNSGlobalSettingsPageInfo({
          pageId: 'ON_DEMAND_NS_ALERTS_LIST'
        })
      );
    }
    dispatch(getCustomerTypesSegment());
  }, [dispatch, widgetId]);

  useEffect(() => {
    if (isVisible && activeTab == 4) {
      const filterQueries = prepareAlertHistoryFilters(filters, jobId);
      dispatch(
        getInvestListData({
          sourceIds: '27,28',
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          segmentName: filters.segmentName,
          scoreLow: filters.alertScore?.[0],
          scoreHigh: filters.alertScore?.[1],
          isSentToCm: filters.alertStatus,
          partySearch: filters.searchValue || undefined,
          q: btoaUTF8(
            JSON.stringify({
              filter: filterQueries,
              sort: {
                columnName: 'alertDate',
                operator: filters.sortOrder
              }
            })
          )
        })
      );
    }
  }, [isVisible, filters, pageSize, currentPage, jobId, activeTab, dispatch]);

  useEffect(() => {
    if (hasError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasError]);

  const tableColumns = useMemo(
    () =>
      genarateTableConfigs({
        settings: widgetConfig,
        configs: result(ComponentsMap, 'NS_IAD.ScreeningAlertList') || {}
      }),
    [widgetConfig]
  );

  const onPageChange = ({ current }) => {
    dispatch(updateAlertListPage(current));
  };

  const onShowSizeChange = (current, page) => {
    dispatch(updateAlertListPageSize(page));
  };

  const pagination = useMemo(
    () => ({
      total,
      current: currentPage,
      pageSize,
      showTotal: (total) => (
        <TablePagination
          selectedItemsCount={selectedIds.length}
          total={total}
          currentPage={currentPage}
          pageSize={pageSize}
        />
      ),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onShowSizeChange
    }),
    [total, currentPage, selectedIds, pageSize]
  );

  const rowSelection = useMemo(
    () => ({
      type: 'checkbox',
      selectedRowKeys: selectedIds,
      onChange: (ids) => {
        let alertsSelected = selectedAlerts.filter(({ alertId }) =>
          ids.includes(alertId)
        );
        const alertsToAdd = ids.filter(
          (id) => !selectedAlerts.some(({ alertId }) => id === alertId)
        );

        if (alertsToAdd.length) {
          alertsSelected = [
            ...alertsSelected,
            ...alertList.filter(({ alertId }) => alertsToAdd.includes(alertId))
          ];
        }
        dispatch(
          selectedAlertIds({ selectedIds: ids, selectedAlerts: alertsSelected })
        );
      },
      preserveSelectedRowKeys: true,
      columnWidth: 24
    }),
    [selectedIds, selectedAlerts, alertList, dispatch]
  );

  const onClickLabel = useCallback((clickedItem, id, code) => {
    setRunInstanceId(id);
    setSegmentCode(code || 'default-segment');
    setCurrentDrawerItem(clickedItem);
    setIsOpenDrawer(true);
  }, []);

  const onSendToCMClick = useCallback(
    (alert) => {
      confirm({
        title: 'Send to CM',
        content: 'Are you sure you want to generate an alert in CM?',
        centered: true,
        okText: 'Confirm',
        width: 500,
        okButtonProps: {
          style: {
            backgroundColor: '#2B73E0'
          }
        },
        onOk: () => {
          dispatch(
            sendToCMAlerts({
              ids: [alert.alertId],
              isFromAlertList: true
            })
          );
        }
      });
    },
    [dispatch]
  );

  const onCloseDrawer = () => {
    setIsOpenDrawer(false);
    setRunInstanceId('');
    setSegmentCode('');
    setCurrentDrawerItem('');
  };

  const preparedData = useMemo(
    () =>
      alertList?.map((alertItem) => ({
        ...alertItem,
        onClickLabel,
        onSendToCMClick: () => onSendToCMClick(alertItem)
      })),
    [alertList, onClickLabel, onSendToCMClick]
  );

  const onClearJobId = () => {
    setJobId(null);
    history.replace({ ...history.location, state: undefined });
  };

  return (
    <Spin spinning={isLoading}>
      {hasError && <StyledAlert type="error" message={errorMsg} />}
      <AlertListFilters
        dispatch={dispatch}
        selectedIds={selectedIds}
        selectedAlerts={selectedAlerts}
        filters={filters}
        jobId={jobId}
        onClearJobId={onClearJobId}
        customerType={customerType}
        widgetConfig={widgetConfig}
      />
      <StyledTable
        rowKey="alertId"
        columns={tableColumns}
        dataSource={preparedData}
        loading={isLoadingAlerts}
        onChange={onPageChange}
        pagination={pagination}
        rowSelection={rowSelection}
        onRow={(record) => ({
          onClick: () =>
            history.push(`${ROUTE_AMLS_SMART_SCREENING}/alert/${record?.alertId}/4`)
        })}
      />
      <Drawer
        isOpen={isOpenDrawer}
        runInstanceId={runInstanceId}
        segmentCode={segmentCode}
        visibleItem={currentDrawerItem}
        onClose={onCloseDrawer}
      />
    </Spin>
  );
};

AlertList.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  settings: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  isLoadingAlerts: PropTypes.bool.isRequired,
  alertList: PropTypes.array,
  customerType: PropTypes.array,
  total: PropTypes.number,
  hasError: PropTypes.bool,
  errorMsg: PropTypes.string,
  selectedIds: PropTypes.array,
  selectedAlerts: PropTypes.array,
  filters: PropTypes.object,
  activeTab: PropTypes.number
};

const mapStateToProps = ({
  amlsCommon,
  settings,
  nsInvestigation,
  nsOnDemandScreening
}) => {
  const { isLoading: isLoadingAlertLevel } = amlsCommon.alertLevelConfigs;
  const { data, isLoading: isLoadingSettings } = settings?.nsSetting;
  const { isLoading: isLoadingTable } = settings?.configuration;
  const {
    alertList: { selectedIds, selectedAlerts, currentPage, pageSize, filters }
  } = nsOnDemandScreening;
  const {
    alertList,
    customerType,
    alertCounts,
    isLoadingList: isLoadingAlerts,
    hasError,
    error,
    sendToCM
  } = nsInvestigation;

  return {
    settings: data,
    isLoading:
      isLoadingSettings ||
      sendToCM.isLoading ||
      isLoadingTable ||
      isLoadingAlertLevel,
    alertList,
    customerType,
    isLoadingAlerts,
    total: alertCounts?.all || 0,
    hasError,
    errorMsg: error,
    selectedIds,
    selectedAlerts,
    currentPage,
    pageSize,
    filters
  };
};

export default connect(mapStateToProps)(AlertList);
